import { useState } from "react";
import { useAtomValue } from "jotai";
import { shouldOfferInStockFilter } from "@models/productFacet";
import { SortPanel } from "./SortPanel";
import { FilterPanel } from "./FilterPanel";
import { useSearchFilters } from "./useSearchFilters";
import {
    productFacetsAtom,
    productFacetsStatusAtom,
} from "@/store/productFacetAtoms";

interface SortFilterSidebarProps {
    /** False on a category page, where the route fixes the category (#401 B4). */
    showCategories?: boolean;
    /** False on a vendor page, where the route fixes the vendor (#425). */
    showVendors?: boolean;
}

// Desktop-only left rail (hidden at < md). Selections live-update the URL
// via useSearchFilters — no Apply button, unlike the mobile sheets.
export const SortFilterSidebar = ({
    showCategories = true,
    showVendors = true,
}: SortFilterSidebarProps = {}) => {
    const filters = useSearchFilters();
    // Facet values are fetched once by the results page; the rail only reads them.
    const facets = useAtomValue(productFacetsAtom);
    const facetsStatus = useAtomValue(productFacetsStatusAtom);

    // The "an active filter always keeps a control that can clear it" exception is read ONCE, from
    // the value this page arrived with — never from the live one. The rail live-updates the URL, so
    // deriving it from `filters.inStockOnly` means switching the toggle off makes
    // shouldOfferInStockFilter go false and unmounts the whole section in the same commit: with the
    // facet read failed or still in flight, the control vanishes from under the pointer, focus
    // drops to <body>, and nothing on screen can turn in-stock filtering back on.
    //
    // FilterSheet makes the same distinction one layer up — committed rather than buffered — for
    // exactly this reason; the rail has no buffer, so its equivalent of "committed" is the value at
    // mount. A read that later succeeds re-opens the control on its own, since "loaded" satisfies
    // shouldOfferInStockFilter regardless of this value.
    const [inStockOnlyOnArrival] = useState(filters.inStockOnly);

    const showInStockOnly = shouldOfferInStockFilter(
        facetsStatus,
        inStockOnlyOnArrival,
    );

    return (
        <aside
            data-testid="sort-filter-sidebar"
            aria-label="Sort and filter"
            className="hidden md:flex flex-col w-56 shrink-0 border-r border-base-200 bg-base-100"
        >
            <SortPanel
                selected={filters.sort}
                onChange={filters.setSort}
            />
            <FilterPanel
                selectedCategoryIds={filters.categoryIds}
                onToggleCategory={filters.toggleCategory}
                maxPrice={filters.maxPrice}
                selectedBrands={filters.brands}
                selectedVendorIds={filters.vendorIds}
                inStockOnly={filters.inStockOnly}
                onMaxPriceChange={filters.setMaxPrice}
                onToggleBrand={filters.toggleBrand}
                onToggleVendor={filters.toggleVendor}
                onInStockOnlyChange={filters.setInStockOnly}
                brands={facets.brands}
                vendors={facets.vendors}
                showInStockOnly={showInStockOnly}
                showCategories={showCategories}
                showVendors={showVendors}
            />
        </aside>
    );
};
